import type { BaseHit } from 'instantsearch.js';
import { useEffect, useMemo, useState } from 'react';
import type { AutosuggestProps } from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import { useDebounce } from 'react-use';
import styled, { css } from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { SearchInput } from 'atoms/inputs/SearchInput';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelS } from 'atoms/typography';
import {
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_TEAMS_SLUG,
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_LEAGUES_SLUG,
} from 'constants/__generated__/routes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useScreenSize } from 'hooks/device/useScreenSize';
import {
  MAX_RESULTS as MAX_RESULTS_FROM_HOOK,
  useLatestSearchItems,
} from 'hooks/search/useLatestSearchItems';
import { useSettlableWalletsSearch } from 'hooks/useSettlableWalletsSearch';
import { useEvents } from 'lib/events/useEvents';
import { glossary } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { getFormattedSport } from 'lib/sports';
import { sportFromJSON } from 'protos/events/shared/events';
import { desktopAndAbove } from 'style/mediaQuery';
import { OverrideClasses } from 'style/utils';

import { CardSuggestion } from '../CardSuggestion';
import { ClubSuggestion } from '../ClubSuggestion';
import { CompetitionSuggestion } from '../CompetitionSuggestion';
import { CountrySuggestion } from '../CountrySuggestion';
import { EmptyResult } from '../EmptyResult';
import { SEARCH_PARAMS } from '../InstantSearch/types';
import { buildFilterQuery } from '../InstantSearch/utils';
import { NationalTeamSuggestion } from '../NationalTeamSuggestion';
import { PlayerSuggestion } from '../PlayerSuggestion';
import { UserSuggestion } from '../UserSuggestion';

export const MAX_RESULTS = MAX_RESULTS_FROM_HOOK;

const getSectionSuggestions = (section: any) => {
  section.hits.forEach((h: any) => {
    h.index = section.index;
  });
  return section.hits;
};

const messages = defineMessages({
  tabsTitle: {
    id: 'MultiSportAutosuggestSearchInput.tabsTitle',
    defaultMessage: 'I’m looking for',
  },
  players: {
    id: 'SearchBar.players',
    defaultMessage: 'Players',
  },
  countries: {
    id: 'SearchBar.countries',
    defaultMessage: 'Countries',
  },
  nationalTeams: {
    id: 'SearchBar.nationalTeams',
    defaultMessage: 'National Teams',
  },
  clubs: {
    id: 'SearchBar.clubs',
    defaultMessage: 'Clubs',
  },
  teams: {
    id: 'SearchBar.teams',
    defaultMessage: 'Teams',
  },
  managers: {
    id: 'SearchBar.managers',
    defaultMessage: 'Managers',
  },
  leagues: {
    id: 'SearchBar.leagues',
    defaultMessage: 'Leagues',
  },
  cards: {
    id: 'SearchBar.cards',
    defaultMessage: 'My cards',
  },
});

const SectionTitle = styled(LabelS)`
  margin-bottom: var(--half-unit);
`;

// https://github.com/styled-components/styled-components/issues/4182
const AutosuggestHelper = <T,>({
  asTheme,
  ...props
}: Omit<AutosuggestProps<T>, 'theme'> & {
  asTheme?: AutosuggestProps<T>['theme'];
}) => {
  return <Autosuggest theme={asTheme} {...props} />;
};

const [StyledAutosuggest, classes] = OverrideClasses(AutosuggestHelper, null, {
  container: css`
    position: relative;
    line-height: 1;
    flex-grow: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 8 * var(--unit));
  `,
  suggestionsContainerOpen: css`
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: auto;
    margin: var(--double-unit) -50px 0;
    .forceSearchbarForDesktop & {
      margin: var(--double-unit) 0 0;
    }
    @media ${desktopAndAbove} {
      margin: var(--double-unit) 0 0;
      width: 100%;
    }
  `,
  suggestionsList: css`
    margin: 0;
    padding: 0;
    list-style-type: none;
  `,
  suggestion: css`
    display: block;
    &:last-child {
      margin-bottom: var(--unit);
    }
  `,
});
const AutosuggestContainer = styled(Vertical).attrs({ gap: 0 })`
  gap: 10px;
`;
const TabsContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  margin-left: -50px;
  .forceSearchbarForDesktop & {
    margin-left: 0;
  }
  @media ${desktopAndAbove} {
    margin-left: 0;
  }
`;
const Tabs = styled.div`
  display: flex;
  gap: 10px;
`;

type Props = {
  onExit: boolean;
  onSelect?: () => void;
  onClose?: () => void;
  selectedSport: Sport;
  sortedSports: Sport[];
  selectSport: (sportInput: Sport, searchTerm: string) => void;
  presetSearch?: string;
  setDisplayLatestSearchItem?: (value: boolean) => void;
};
export const SearchBar = ({
  onExit,
  onSelect = () => {},
  onClose,
  selectSport: doSelectSport,
  selectedSport,
  sortedSports,
  presetSearch,
  setDisplayLatestSearchItem,
}: Props) => {
  const [suggestionValue, setSuggestionValue] = useState<string>();
  const { currentUser } = useCurrentUserContext();
  const { up: isTabletAndAbove } = useScreenSize('tablet');

  const defaultSettlementWallets = useSettlableWalletsSearch();
  const { refine, query: currentSearch } = useSearchBox();
  const { truncatedLatestSearchedItems, addItemToLatestSearch } =
    useLatestSearchItems(selectedSport);
  const { scopedResults, results: searchResults, status } = useInstantSearch();
  const track = useEvents();

  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [search, setSearch] = useState(presetSearch || currentSearch);
  const { algoliaIndexes } = useConfigContext();
  const handleSuggestionsClearRequested = () => {};
  const shouldRenderSuggestions = () => true;
  const [hasNotClickedResult, setHasNotClickedResult] = useState<boolean>(true);

  const searchWithOnSale = buildFilterQuery({
    [SEARCH_PARAMS.ON_SALE]: true,
    ...(defaultSettlementWallets
      ? { [SEARCH_PARAMS.PURCHASE_OPTIONS]: defaultSettlementWallets }
      : {}),
  });

  const getMaxResults = (index: string, defaultValue = 2) => {
    const formerSearchesInThisIndex = truncatedLatestSearchedItems.filter(
      latestSearchedItem => latestSearchedItem.index === index
    ).length;

    if (formerSearchesInThisIndex > defaultValue) {
      // cap it to max 4 results
      return Math.min(formerSearchesInThisIndex, 4);
    }
    return defaultValue;
  };

  const indexConfig = {
    [algoliaIndexes.Player]: {
      anyRoute: ANY_SPORT_PLAYERS_SLUG,
      search: currentUser ? searchWithOnSale : undefined,
      title: formatMessage(messages.players),
      suggestion: PlayerSuggestion,
      maxResults: getMaxResults(algoliaIndexes.Player, 4),
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes.Country]: {
      routes: {
        [Sport.FOOTBALL]: FOOTBALL_COUNTRIES_COUNTRYCODE,
        [Sport.BASEBALL]: undefined,
        [Sport.NBA]: undefined,
      },
      title: formatMessage(messages.countries),
      suggestion: CountrySuggestion,
      maxResults: getMaxResults(algoliaIndexes.Country),
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes['National Team']]: {
      routes: {
        [Sport.FOOTBALL]: FOOTBALL_COUNTRIES_COUNTRYCODE,
        [Sport.BASEBALL]: undefined,
        [Sport.NBA]: undefined,
      },
      title: formatMessage(messages.nationalTeams),
      suggestion: NationalTeamSuggestion,
      maxResults: getMaxResults(algoliaIndexes['National Team']),
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes.Club]: {
      anyRoute: ANY_SPORT_TEAMS_SLUG,
      search: currentUser ? searchWithOnSale : undefined,
      titles: {
        [Sport.FOOTBALL]: formatMessage(messages.clubs),
        [Sport.BASEBALL]: formatMessage(messages.teams),
        [Sport.NBA]: formatMessage(messages.teams),
      },
      suggestion: ClubSuggestion,
      maxResults: getMaxResults(algoliaIndexes.Club),
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes.User]: {
      anyRoute: ANY_SPORT_MY_CLUB_SLUG,
      title: formatMessage(messages.managers),
      suggestion: UserSuggestion,
      maxResults: getMaxResults(algoliaIndexes.User),
      hideWithoutQuery:
        truncatedLatestSearchedItems.findIndex(
          truncatedLatestSearchedItem =>
            truncatedLatestSearchedItem.index === algoliaIndexes.User
        ) === -1,
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes.Competition]: {
      routes: {
        [Sport.FOOTBALL]: FOOTBALL_LEAGUES_SLUG,
        [Sport.BASEBALL]: undefined,
        [Sport.NBA]: undefined,
      },
      search: currentUser ? searchWithOnSale : undefined,
      title: formatMessage(messages.leagues),
      suggestion: CompetitionSuggestion,
      maxResults: getMaxResults(algoliaIndexes.Competition),
      getSlug: (hit: BaseHit) => hit.objectID,
    },
    [algoliaIndexes.New]: {
      anyRoute: ANY_SPORT_CARDS_SLUG,
      title: formatMessage(messages.cards),
      suggestion: CardSuggestion,
      maxResults: getMaxResults(algoliaIndexes.New),
      hideWithoutQuery: false,
      getSlug: (hit: BaseHit) => hit.slug,
    },
  };

  const getConfig = (index: keyof typeof indexConfig) => {
    const result = indexConfig[index];
    if (result) {
      return result!;
    }
    throw new Error(`Missing config for ${index}`);
  };

  const getLink = (s: BaseHit) => {
    const config = getConfig(s.index);
    const slug = config.getSlug(s);

    const route = config?.routes?.[selectedSport];
    const anyRoute = config?.anyRoute;

    if (!route && !anyRoute) {
      throw new Error(`Missing route config for ${s.index}/${selectedSport}`);
    }
    let path;
    if (route) {
      if (route === FOOTBALL_COUNTRIES_COUNTRYCODE) {
        path = generatePath(route, { countryCode: s.country.code });
      } else {
        path = generatePath(route, { slug });
      }
    }
    if (anyRoute) {
      path = generateSportPath(anyRoute, {
        params: {
          slug,
        },
        sport: selectedSport,
      });
    }
    return `${path}${config.search ? `?${config.search}` : ''}`;
  };

  const renderSectionTitle = (section: any) => {
    const config = getConfig(section.index);
    const title = config.title || config.titles?.[selectedSport];

    return section.hits.length > 0 ? (
      <SectionTitle color="var(--c-nd-600)">{title}</SectionTitle>
    ) : null;
  };

  // First iteration, we stick to legacy search inputs
  const hits = scopedResults
    .map(result => {
      const { results } = result;
      if (!results) {
        return null;
      }
      const config = getConfig(results.index);

      return {
        index: result.indexId,
        hits:
          config.hideWithoutQuery && !search
            ? []
            : results.hits.slice(0, config.maxResults).map(h => ({
                ...h,
                index: result.indexId,
              })),
      };
    })
    .filter(Boolean);

  // since we are querying multiple indexes, hits has the following structure:
  // [
  //    { index: 'Player', hits: [{ display_name: 'Kylian Mbappé' }, { display_name: 'Antoine Griezmann' }]},
  //    { index: 'Club', hits: [{ name: 'PSG' }]}
  // ]
  const results: typeof hits = JSON.parse(JSON.stringify(hits));
  const nbIndex = results.length;
  let nbResults = results.reduce((acc, index) => acc + index.hits.length, 0);
  let iteration = 0;
  // remove the last hit from each index alternatively until the number of results is correct
  while (nbResults > MAX_RESULTS) {
    const indexHits = results[iteration % nbIndex].hits;
    if (indexHits.length > 0) {
      if (
        !truncatedLatestSearchedItems.find(
          latestSearchedItem =>
            latestSearchedItem.objectID ===
              indexHits[indexHits.length - 1].objectID &&
            indexHits[indexHits.length - 1].index === latestSearchedItem.index
        )
      ) {
        (indexHits as any).splice(-1);
        nbResults -= 1;
      }
    }
    iteration += 1;
  }

  const isLoadingAfterChangingTab = useMemo(
    () => presetSearch && !searchResults?.query,
    [presetSearch, searchResults?.query]
  );

  const emptyResult = useMemo(() => {
    if (status === 'loading' || !currentSearch) return false;
    if (isLoadingAfterChangingTab) return false;
    if (nbResults === 0) return true;
    return false;
  }, [isLoadingAfterChangingTab, nbResults, status, currentSearch]);

  const selectSport = (sport: Sport, searchTerm: string) => {
    track('Click Filter In Search', {
      searchTerm,
      context: sportFromJSON(selectedSport),
      sport,
    });
    doSelectSport(sport, searchTerm);
  };

  useEffect(() => {
    return () => {
      if (hasNotClickedResult && onExit) {
        track('Exit Search Without Clicking Result', {
          searchTerm: search,
          context: sportFromJSON(selectedSport),
        });
      }
    };
    // do not include `search` in the dependencies, we don't want this to be
    // triggered at each keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExit, hasNotClickedResult, track]);

  useDebounce(
    () => {
      if (suggestionValue !== undefined) {
        setDisplayLatestSearchItem?.(
          suggestionValue === '' || suggestionValue === undefined
        );
        refine(suggestionValue);
      }
    },
    300,
    [suggestionValue, setDisplayLatestSearchItem]
  );

  return (
    <StyledAutosuggest
      highlightFirstSuggestion
      multiSection
      alwaysRenderSuggestions
      asTheme={classes}
      suggestions={isLoadingAfterChangingTab ? [] : results}
      onSuggestionsFetchRequested={({ value }) => setSuggestionValue(value)}
      onSuggestionsClearRequested={handleSuggestionsClearRequested}
      onSuggestionSelected={(event, { suggestion }) => {
        const destination = getLink(suggestion);
        setHasNotClickedResult(false);
        track('Click Search Result', {
          resultCategory: suggestion.index,
          resultDestination: destination,
          searchTerm: search,
          context: sportFromJSON(selectedSport),
        });
        addItemToLatestSearch(suggestion);
        navigate(destination);
        onSelect();
      }}
      getSuggestionValue={() => search}
      shouldRenderSuggestions={shouldRenderSuggestions}
      renderSuggestion={(hit: any, { isHighlighted }) => {
        const { index } = hit;
        const SuggestionComponent = getConfig(index).suggestion;

        return (
          <SuggestionComponent
            key={hit.objectID}
            hit={hit}
            isHighlighted={isHighlighted}
            onClose={onClose}
          />
        );
      }}
      inputProps={{
        value: search,
        placeholder: formatMessage(glossary.search),
        onChange: (event, { newValue }) => {
          setSearch(newValue);
        },
        autoFocus: true,
      }}
      renderInputComponent={({
        onChange,
        value,
        withIcon = false,
        ref,
        type,
        ...rest
      }) => (
        <AutosuggestContainer>
          <Horizontal>
            <SearchInput
              withIcon={withIcon}
              fullWidth
              onChange={event =>
                onChange(event, {
                  newValue: event.target.value,
                  method: 'type',
                })
              }
              inputRef={ref}
              {...rest}
              value={value}
            />
            {!!onClose && !isTabletAndAbove && (
              <Button onClick={onClose} size="medium" color="transparent">
                <FormattedMessage {...glossary.cancel} />
              </Button>
            )}
          </Horizontal>
          {!!sortedSports && sortedSports.length > 0 && (
            <TabsContainer>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...messages.tabsTitle} />
              </LabelS>
              <Tabs>
                {sortedSports.map((sport: Sport) => (
                  <Button
                    key={sport}
                    size="medium"
                    color={sport === selectedSport ? 'secondary' : 'tertiary'}
                    onClick={() => selectSport(sport, value)}
                  >
                    {getFormattedSport(sport)}
                  </Button>
                ))}
              </Tabs>

              {!!emptyResult && (
                <Tabs>
                  <EmptyResult sport={selectedSport} />
                </Tabs>
              )}
            </TabsContainer>
          )}
        </AutosuggestContainer>
      )}
      renderSuggestionsContainer={({ containerProps, children }) => {
        if (!currentSearch && nbResults === 0) {
          return <div style={{ height: 778 }} />;
        }
        return <Vertical {...containerProps}>{children}</Vertical>;
      }}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={getSectionSuggestions}
      focusInputOnSuggestionClick={false}
    />
  );
};
